import BottomSheet from '../../Interface/LegacyBottomSheet';
import React, {useState, useRef, useEffect} from 'react';
import {Animated, View, StyleSheet, Dimensions} from 'react-native';

import {useAuth} from '../../../context/auth';
import SigninForm from './components/SigninForm';
import SignupForm from './components/SignupForm';

import ForgotPasswordForm from './components/ForgotPasswordForm';
import {useNavigation, useNavigationState} from '@react-navigation/native';
import {NavigationProp} from '@react-navigation/native';
import {RootStackParamList} from '../../../dtos/RoutesDTO';
import {rastrear} from '../../../services/rastrear';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';

export default function AuthBottomSheet() {
  const {
    showSign,
    setShowSign,
    sessionExpired,
    setSessionExpired,
    signDismissable,
    setSignDismissable,
    forgotPasswordFromChangePassword,
    setForgotPasswordFromChangePassword,
    signStatusTela,
    isLogged,
  } = useAuth();
  const [signType, setSignType] = useState<
    'select' | 'signin' | 'signup' | 'forgot-password'
  >('signin');

  const currentRoute = useNavigationState(state => {
    if (!state) {
      return undefined;
    }

    const getActiveRoute = (navState: any) => {
      if (!navState || navState.index === undefined || !navState.routes) {
        return undefined;
      }

      const route = navState.routes[navState.index];

      if (!route) {
        return undefined;
      }

      if (route.state) {
        return getActiveRoute(route.state);
      }

      return route;
    };

    return getActiveRoute(state);
  });

  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  useEffect(() => {
    if (showSign) {
      if (forgotPasswordFromChangePassword) {
        setSignType('forgot-password');
      } else {
        setSignType(showSign === 'signup' ? 'signup' : 'signin');
      }

      rastrear({status_tela: signStatusTela});
    }
  }, [showSign]);

  const slideAnim = useRef(new Animated.Value(0)).current;
  const screenWidth = Dimensions.get('window').width;

  const animateSlide = (direction: number, callback?: () => void) => {
    // direction: -1 for back, 1 for forward
    Animated.timing(slideAnim, {
      toValue: direction * screenWidth,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      callback?.(); // Call callback after slide animation
      slideAnim.setValue(-direction * screenWidth); // Reset for next slide
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    });
  };

  const handleSignTypeChange = (
    newType: 'select' | 'signin' | 'signup' | 'forgot-password',
  ) => {
    let direction = 1;

    if (
      signType === 'select' &&
      (newType === 'signin' || newType === 'signup')
    ) {
      direction = -1;
    } else if (
      (signType === 'signin' || signType === 'signup') &&
      newType === 'select'
    ) {
      direction = 1;
    } else if (signType === 'signin' && newType === 'signup') {
      direction = -1;
    } else if (signType === 'signin' && newType === 'forgot-password') {
      direction = -1;
    } else if (signType === 'forgot-password' && newType === 'signin') {
      direction = 1;
    }

    animateSlide(direction, () => setSignType(newType));
  };

  const handleClose = () => {
    setShowSign(false);
    setSignType('select');
    setSessionExpired(false);
    setSignDismissable(true);
  };

  const onSuccess = () => {
    setSignDismissable(true);

    if (currentRoute?.name === 'Fuel') {
      if (signStatusTela === 'loginNoFluxo') {
        setTimeout(() => navigation.goBack(), 3000);
      } else {
        navigation.goBack();
      }
    }
  };

  const onForgotPasswordSuccess = () => {
    setShowSign(false);
    setForgotPasswordFromChangePassword(false);
    setSignType('select');
    if (currentRoute?.name === 'ChangePassword') {
      navigation.goBack();
    }
  };

  useEffect(() => {
    if (
      showSign &&
      currentRoute?.name === 'Fuel' &&
      !isLogged &&
      signStatusTela !== 'loginNoFluxo'
    ) {
      navigation.goBack();
    }
  }, [currentRoute?.name, signStatusTela, isLogged, navigation, showSign]);

  useEffect(() => {
    slideAnim.setValue(screenWidth); // Initial slide-in for the first form
    Animated.timing(slideAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, []);

  return (
    <BottomSheet
      isOpen={showSign}
      dismissable={signDismissable}
      onClose={handleClose}>
      <KeyboardAwareScrollView enableAutomaticScroll>
        <View style={styles.animatedContainer}>
          <Animated.View
            style={[
              styles.animatedView,
              {transform: [{translateX: slideAnim}]},
            ]}>
            {signType === 'signin' && (
              <SigninForm
                onBack={handleClose}
                onSuccess={onSuccess}
                onSignup={() => handleSignTypeChange('signup')}
                sessionExpired={sessionExpired}
                onForgotPassword={() => handleSignTypeChange('forgot-password')}
              />
            )}
            {signType === 'forgot-password' && (
              <ForgotPasswordForm
                fromChangePassword={forgotPasswordFromChangePassword}
                onBack={() => {
                  if (forgotPasswordFromChangePassword) {
                    setShowSign(false);
                    setForgotPasswordFromChangePassword(false);
                  } else {
                    handleSignTypeChange('signin');
                  }
                }}
                onSuccess={() => {
                  if (forgotPasswordFromChangePassword) {
                    onForgotPasswordSuccess();
                  } else {
                    handleSignTypeChange('signin');
                  }
                }}
              />
            )}
            {signType === 'signup' && (
              <SignupForm
                onBack={() => handleSignTypeChange('signin')}
                onSuccess={onSuccess}
              />
            )}
          </Animated.View>
        </View>
      </KeyboardAwareScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  animatedContainer: {
    overflow: 'hidden', // Hide components sliding out of bounds
  },
  animatedView: {
    width: '100%',
  },
});
